import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { TickmillCompanyAllowedReferralsDto } from 'api/Trading-Accounts/Types/TickmillCompanyAllowedReferralsDto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { AddEditIBEntityForm, AddEditIBEntityFormValues } from './AddEditIBEntityForm'
import { normalizeReferralsRestrictionsPageSearchQuery } from './normalizer/normalizeReferralsRestrictionsPageSearchQuery'
import referralsRestrictionsColumns from './referralsRestrictionsColumns'

const useFilter = () => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const isFirstRun = useRef(true)
  const [data, setData] = useState<TickmillCompanyAllowedReferralsDto[]>([])

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [totalResults, setTotalResults] = useState<number>(0)
  const [isLoadingFilter, setLoading] = useState<boolean>(false)

  const getSearchQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeReferralsRestrictionsPageSearchQuery(filterDescriptor))
  }, [])

  const fetchFilter = async () => {
    setLoading(true)
    const searchQuery = getSearchQuery(filter)
    const pageIndex = 1
    const pageSize = paginationState.take

    const response = await partnershipsApiClient.getIntroducingBrokersCompanyReferrals({
      search: searchQuery,
      pageIndex,
      pageSize,
      skipCount: tableData?.skipCount || false,
    })

    if (response?.items) {
      setData(response.items)
      setPaginationState({
        skip: 0,
        take: pageSize,
      })
      setTotalResults(response.totalResults)
    }
    setTableData({ ...tableData, meta: response })
    setLoading(false)
  }

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }

    const timeoutId = setTimeout(() => fetchFilter(), 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoadingFilter,
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    data,
    setData,
    fetchFilter,
    tableData,
    setTableData,
  }
}

const ReferralsRestrictionsPage = (): JSX.Element => {
  const [auth] = useContext(AuthSessionContext)
  const setToast = useContext(ToastContext)
  const permissions = auth?.permissions || []

  const [showAddEditIBEntity, setAddEditIBEntity] = useState<{
    entity?: TickmillCompanyAllowedReferralsDto
    visible: boolean
  }>({
    visible: false,
  })
  const [entityToDelete, setEntityToDelete] = useState<{
    data: TickmillCompanyAllowedReferralsDto | null
    visible: boolean
  }>({
    data: null,
    visible: false,
  })

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const [isLoading, setLoading] = useState<boolean>(true)

  const dispatch = useAppDispatch()

  const {
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    data,
    setData,
    tableData,
    setTableData,
  } = useFilter()

  const getQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeReferralsRestrictionsPageSearchQuery(filterDescriptor))
  }, [])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      await fetchCompanyReferrals()
      if (isEmpty(tickmillCompanies)) {
        await dispatch(fetchTickmillCompaniesAction(identityApiClient))
      }
      setLoading(false)
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (entityToDelete.data) {
      try {
        await partnershipsApiClient.deleteIntroducingBrokersCompanyReferral(
          entityToDelete.data.tickmillCompany.id,
          entityToDelete.data.allowedCompany.id
        )
        setToast(successToast('Entity deleted.'))
        await fetchCompanyReferrals()
        handleConfirmationCancel()
      } catch (error) {
        console.error(error)
        handleConfirmationCancel()
      }
    }
  }

  const handleConfirmationCancel = () => {
    setEntityToDelete({ visible: false, data: null })
  }

  const onSubmit = async (values: AddEditIBEntityFormValues) => {
    await partnershipsApiClient.addIntroducingBrokersCompanyReferral(values)
    setToast(successToast('Entity added.'))
    setAddEditIBEntity({ entity: undefined, visible: false })
    await fetchCompanyReferrals()
  }

  const handleDeleteConfirmation = (entity: TickmillCompanyAllowedReferralsDto) => {
    setEntityToDelete({ data: entity, visible: true })
  }

  const fetchCompanyReferrals = async () => {
    const response = await partnershipsApiClient.getIntroducingBrokersCompanyReferrals({
      pageIndex: 1,
      pageSize: 10,
    })
    if (response?.items) {
      setData(response.items)
      setPaginationState(paginationState)
      setTotalResults(response.totalResults)
    }
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery(filter)

    const response = await partnershipsApiClient.getIntroducingBrokersCompanyReferrals({
      search: searchQuery,
      pageIndex: tableData?.meta?.pageIndex,
      pageSize: tableData?.meta?.pageSize || 10,
      skipCount,
    })
    if (response?.items) {
      setData(response.items)
    }

    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    const searchQuery = getQuery(filter)
    const response = await partnershipsApiClient.getIntroducingBrokersCompanyReferrals({
      search: searchQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount,
    })

    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageChange = async (page: number) => {
    const searchQuery = getQuery(filter)
    const pageIndex = page

    const response = await partnershipsApiClient.getIntroducingBrokersCompanyReferrals({
      search: searchQuery,
      pageIndex,
      pageSize: tableData?.meta?.pageSize,
      skipCount: tableData?.skipCount,
    })
    if (response?.items) {
      setData(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    setFilter({
      ...event.filter,
      filters: event?.filter?.filters || [],
    })
  }

  const handleAddIbEntityModal = () => {
    setAddEditIBEntity({ visible: true })
  }

  return (
    <>
      {showAddEditIBEntity.visible && (
        <AddEditIBEntityForm
          isOpen={showAddEditIBEntity.visible}
          onClose={() => setAddEditIBEntity({ visible: false, entity: undefined })}
          entity={showAddEditIBEntity.entity}
          tickmillCompanies={tickmillCompanies}
          onSubmit={onSubmit}
        />
      )}
      {entityToDelete.data && (
        <ConfirmationModal
          yesButtonText='Yes, delete'
          onYes={handleDelete}
          onCancel={() => setEntityToDelete({ data: null, visible: false })}
          visible={entityToDelete.visible}
          renderTitle={
            <div>
              <p>
                Are you sure you want to
                <br />
                delete the following entity
              </p>

              <div>
                <div id='field'>
                  <strong>Tickmill Company</strong> {entityToDelete.data.tickmillCompany.name}
                </div>
                <div id='field'>
                  <strong>Allowed Tickmill Company</strong>{' '}
                  {entityToDelete.data.allowedCompany.name}
                </div>
              </div>
            </div>
          }
        />
      )}
      <Breadcrumbs
        crumbs={[{ title: 'IB Referrals', link: links.brokerageReferralsRestrictions.url }]}
      />
      <Loading isLoading={isLoading}>
        <AddButton
          text='Add IB Entity'
          onClick={handleAddIbEntityModal}
          disabled={!hasPermission(permissions, PermissionsEnum.CanCreateTickmillCompanyReferrals)}
          data-test='add-entity-button'
        />
        <TableStateContext.Provider value={[tableData, setTableData]}>
          <Grid
            filterable
            data={data}
            filter={filter}
            take={paginationState.take}
            skip={paginationState.skip}
            total={totalResults}
            onFilterChange={handleFilterChange}
            pageable={true}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={tableData?.meta?.pageIndex || 1}
                pageSize={tableData?.meta?.pageSize || 10}
                take={paginationState.take}
                skip={paginationState.skip}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {referralsRestrictionsColumns
              .filter((c) => c.show !== false)
              .map((column, idx) => (
                <Column key={idx} {...column} />
              ))}
            <Column
              width={50}
              title=''
              filterCell={() => null}
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Delete',
                        disabled: !hasPermission(
                          permissions,
                          PermissionsEnum.CanDeleteTickmillCompanyReferrals
                        ),
                        onClick: () => handleDeleteConfirmation(gridCellProps.dataItem),
                      },
                    ]}
                  />
                </td>
              )}
            />
          </Grid>
        </TableStateContext.Provider>
      </Loading>
    </>
  )
}

export default ReferralsRestrictionsPage
